import { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUsers, FaCalendarCheck, FaCalendarTimes, FaClock, FaSignInAlt, FaSignOutAlt, FaUserCheck, FaBan } from 'react-icons/fa';
import { formatDate, formatTime, formatJamKerja } from '../../helper/helper';

const DashboardAdmin = ({ data }) => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalHadirHariIni: 0,
        totalHadirBulanIni: 0,
        totalIzin: 0,
        totalCuti: 0,
        totalAbsensiHariIni: 0
    });
    const [absensiHariIni, setAbsensiHariIni] = useState([]);
    const [absensiTerbaru, setAbsensiTerbaru] = useState([]);
    const [loading, setLoading] = useState(true);

    const header = {
        withCredentials: true
    };

    const getDashboardData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/absensi/admin/dashboard`, header);
            const dashboardData = response.data;

            // Set statistik
            setStats({
                totalUsers: dashboardData.stats?.totalUsers || 0,
                totalHadirHariIni: dashboardData.stats?.totalHadirHariIni || 0,
                totalHadirBulanIni: dashboardData.stats?.totalHadirBulanIni || 0,
                totalIzin: dashboardData.stats?.totalIzin || 0,
                totalCuti: dashboardData.stats?.totalCuti || 0,
                totalAbsensiHariIni: dashboardData.stats?.totalAbsensiHariIni || 0
            });

            // Set absensi hari ini
            setAbsensiHariIni(dashboardData.absensiHariIni || []);

            // Set absensi terbaru
            setAbsensiTerbaru(dashboardData.absensiTerbaru || []);
        } catch (error) {
            console.error('Error fetching admin dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getDashboardData();
        // eslint-disable-next-line
    }, []);

    const statCards = [
        {
            title: 'Total User',
            value: stats.totalUsers,
            icon: FaUsers,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100'
        },
        {
            title: 'Hadir Hari Ini',
            value: stats.totalHadirHariIni,
            icon: FaUserCheck,
            color: 'text-green-600',
            bgColor: 'bg-green-100'
        },
        {
            title: 'Hadir Bulan Ini',
            value: stats.totalHadirBulanIni,
            icon: FaCalendarCheck,
            color: 'text-purple-600',
            bgColor: 'bg-purple-100'
        },
        {
            title: 'Izin',
            value: stats.totalIzin,
            icon: FaCalendarTimes,
            color: 'text-orange-600',
            bgColor: 'bg-orange-100'
        },
        {
            title: 'Cuti',
            value: stats.totalCuti,
            icon: FaBan,
            color: 'text-red-600',
            bgColor: 'bg-red-100'
        },
        {
            title: 'Total Absensi Hari Ini',
            value: stats.totalAbsensiHariIni,
            icon: FaClock,
            color: 'text-indigo-600',
            bgColor: 'bg-indigo-100'
        }
    ];

    return (
        <div className="py-5 mb-20 min-h-[calc(100vh-80px)]">
            <main className="h-full overflow-y-auto">
                <div className="px-4 mx-auto max-w-7xl">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-8 md:p-10 rounded-zafu-xl overflow-hidden bg-gradient-to-br from-zafu-green to-zafu-green-soft text-white shadow-zafu-lg">
                            <div>
                                <p className="text-white/70 text-xs tracking-[0.2em] uppercase mb-2">Zafu Residence</p>
                                <h1 className="font-display text-3xl md:text-4xl font-semibold">Panel Admin</h1>
                                <p className="text-sm text-white/80 mt-2 max-w-md">
                                    Selamat datang, {data.user?.nama || 'Admin'}. Kelola absensi staff dan laporan kehadiran.
                                </p>
                            </div>
                            <div className="mt-6 md:mt-0 text-left md:text-right">
                                <p className="text-xs text-white/60 uppercase tracking-wider">Role</p>
                                <p className="font-medium text-white">{data.user?.role || 'Admin'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Statistik Cards */}
                    <div className="grid gap-3 mb-8 md:grid-cols-4 grid-cols-2">
                        {statCards.map((stat, index) => {
                            const Icon = stat.icon;
                            return (
                                <div key={index} className="flex items-center p-4 bg-white rounded-zafu-xl overflow-hidden shadow-zafu border border-zafu-cream-dark/50">
                                    <div className={`p-3 mr-4 ${stat.bgColor} ${stat.color} rounded-2xl`}>
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="mb-1 text-sm font-medium text-gray-600">
                                            {stat.title}
                                        </p>
                                        {loading ? (
                                            <div className="h-6 w-12 bg-gray-200 rounded animate-pulse"></div>
                                        ) : (
                                            <p className="text-2xl font-bold text-gray-800">
                                                {stat.value}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                        {/* Absensi Hari Ini */}
                        <div className="bg-white rounded-3xl overflow-hidden p-6" style={{ boxShadow: '0 12px 30px rgba(0, 0, 0, 0.05)' }}>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <FaClock className="mr-2 text-[#1B3C33]" />
                                Absensi Hari Ini
                            </h2>
                            {loading ? (
                                <div className="space-y-3">
                                    <div className="h-16 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-16 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-16 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            ) : absensiHariIni.length > 0 ? (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {absensiHariIni.map((item, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                                            <div className="flex-1">
                                                <p className="flex text-sm font-semibold text-gray-800">{item.nama || item.username || '-'}</p>
                                                <p className="flex text-xs text-gray-500">{item.namaDevisi || '-'}</p>
                                                <div className="flex gap-3  mt-2 flex-wrap">
                                                    {item.waktuMasuk && (
                                                        <span className="text-xs text-green-600 flex items-center">
                                                            <FaSignInAlt className="mr-1" />
                                                            {formatTime(item.waktuMasuk)}
                                                        </span>
                                                    )}
                                                    {item.waktuKeluar && (
                                                        <span className="text-xs text-blue-600 flex items-center">
                                                            <FaSignOutAlt className="mr-1" />
                                                            {formatTime(item.waktuKeluar)}
                                                        </span>
                                                    )}
                                                    {item.jumlahJamKerja && (
                                                        <span className="text-xs text-purple-600 flex items-center">
                                                            <FaClock className="mr-1" />
                                                            {formatJamKerja(item.jumlahJamKerja)}
                                                        </span>
                                                    )}
                                                    {item.status && (
                                                        <span className="text-xs text-orange-600">
                                                            {item.status}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={`ml-3 p-2 rounded-full ${item.waktuMasuk && item.waktuKeluar && !item.status
                                                ? 'bg-green-100 text-green-600'
                                                : item.status
                                                    ? 'bg-orange-100 text-orange-600'
                                                    : 'bg-yellow-100 text-yellow-600'
                                                }`}>
                                                {item.waktuMasuk && item.waktuKeluar && !item.status ? (
                                                    <FaCalendarCheck />
                                                ) : (
                                                    <FaCalendarTimes />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <FaCalendarTimes className="mx-auto text-4xl mb-2 text-gray-400" />
                                    <p>Belum ada absensi hari ini</p>
                                </div>
                            )}
                        </div>

                        {/* Absensi Terbaru */}
                        <div className="bg-white rounded-3xl overflow-hidden p-6" style={{ boxShadow: '0 12px 30px rgba(0, 0, 0, 0.05)' }}>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <FaCalendarCheck className="mr-2 text-[#1B3C33]" />
                                Absensi Terbaru
                            </h2>
                            {loading ? (
                                <div className="space-y-3">
                                    <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            ) : absensiTerbaru.length > 0 ? (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {absensiTerbaru.map((item, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-gray-800 flex">{item.nama || item.username || '-'}</p>
                                                <p className="text-xs text-gray-500 flex">{item.namaDevisi || '-'}</p>
                                                <p className="text-xs text-gray-600 mt-1 flex">{formatDate(item.tanggal)}</p>
                                                <div className="flex gap-3 mt-2 flex-wrap">
                                                    {item.waktuMasuk && (
                                                        <span className="text-xs text-green-600 flex items-center">
                                                            <FaSignInAlt className="mr-1" />
                                                            {formatTime(item.waktuMasuk)}
                                                        </span>
                                                    )}
                                                    {item.waktuKeluar && (
                                                        <span className="text-xs text-blue-600 flex items-center">
                                                            <FaSignOutAlt className="mr-1" />
                                                            {formatTime(item.waktuKeluar)}
                                                        </span>
                                                    )}
                                                    {item.jumlahJamKerja && (
                                                        <span className="text-xs text-purple-600 flex items-center">
                                                            <FaClock className="mr-1" />
                                                            {formatJamKerja(item.jumlahJamKerja)}
                                                        </span>
                                                    )}
                                                    {item.status && (
                                                        <span className="text-xs text-orange-600">
                                                            {item.status}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={`ml-3 p-2 rounded-xl ${item.waktuMasuk && item.waktuKeluar && !item.status
                                                ? 'bg-green-100 text-green-600'
                                                : item.status
                                                    ? 'bg-orange-100 text-orange-600'
                                                    : 'bg-yellow-100 text-yellow-600'
                                                }`}>
                                                {item.waktuMasuk && item.waktuKeluar && !item.status ? (
                                                    <div className='flex flex-col items-center justify-center'>
                                                        <FaCalendarCheck />

                                                    </div>


                                                ) : (
                                                    <FaCalendarTimes />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <FaCalendarTimes className="mx-auto text-4xl mb-2 text-gray-400" />
                                    <p>Belum ada data absensi</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default DashboardAdmin;
